import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Pencil, Loader2, Search, X, Power,
  CheckCircle2, XCircle, Shield, ShieldCheck, User as UserIcon,
  ArrowUp, ArrowDown, ChevronDown, Filter,
} from 'lucide-react';
import {
  listUsers,
  updateUser,
  type AdminUser,
  type WhitelistedDomain,
} from '../../services/api';
import { useAdminData } from './AdminDataContext';

const ROLE_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  superadmin: { label: 'Superadmin', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: ShieldCheck },
  admin: { label: 'Admin', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Shield },
  user: { label: 'User', color: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400', icon: UserIcon },
};

type SortCol = 'email' | 'role' | 'status' | 'lastLogin' | 'created';
type SortDir = 'asc' | 'desc';

function ModalPortal({ children }: { children: React.ReactNode }) {
  return ReactDOM.createPortal(children, document.body);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
}

// ─── Sortable Header ──────────────────────────────────────────────────────
function SortHeader({
  label,
  col,
  sortCol,
  sortDir,
  onSort,
  className,
}: {
  label: string;
  col: SortCol;
  sortCol: SortCol;
  sortDir: SortDir;
  onSort: (col: SortCol) => void;
  className?: string;
}) {
  const active = sortCol === col;
  return (
    <th
      className={`px-4 py-3 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 transition-colors ${className || ''}`}
      onClick={() => onSort(col)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        {active ? (
          sortDir === 'asc'
            ? <ArrowUp className="w-3 h-3" />
            : <ArrowDown className="w-3 h-3" />
        ) : (
          <ArrowDown className="w-3 h-3 opacity-0 group-hover:opacity-30" />
        )}
      </div>
    </th>
  );
}

// ─── Column Filter Dropdown ───────────────────────────────────────────────
function ColumnFilterDropdown({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLTableCellElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        ref.current && !ref.current.contains(e.target as Node) &&
        dropRef.current && !dropRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const hasFilter = value !== 'all';
  const selectedLabel = options.find((o) => o.value === value)?.label || label;

  return (
    <th ref={ref} className={`px-4 py-3 relative ${className || ''}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors ${
          hasFilter
            ? 'text-blue-600 dark:text-blue-400'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
        }`}
      >
        {hasFilter ? selectedLabel : label}
        {hasFilter ? (
          <Filter className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )}
      </button>
      {open && ReactDOM.createPortal(
        (() => {
          const rect = ref.current?.getBoundingClientRect();
          if (!rect) return null;
          return (
            <div
              ref={dropRef}
              style={{ position: 'fixed', top: rect.bottom + 4, left: rect.left, zIndex: 9999, minWidth: 140 }}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden"
            >
              {options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    value === opt.value
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          );
        })(),
        document.body,
      )}
    </th>
  );
}

// ─── Permission helpers ───────────────────────────────────────────────────
function canEditUser(callerRole: string, target: AdminUser): boolean {
  if (callerRole === 'superadmin') return true;
  if (callerRole === 'admin' && target.role === 'user') return true;
  return false;
}

function canDeactivateUser(callerRole: string, target: AdminUser, callerId?: number): boolean {
  if (target.id === callerId) return false;
  if (callerRole === 'superadmin') return true;
  if (callerRole === 'admin' && target.role === 'user') return true;
  return false;
}

function canChangeRole(callerRole: string, target: AdminUser): boolean {
  if (target.role === 'superadmin') return false;
  if (callerRole === 'superadmin') return true;
  // Admin can promote user→admin (turing.com only), but cannot demote admin→user
  if (callerRole === 'admin' && target.role === 'user') {
    return target.email.endsWith('@turing.com');
  }
  return false;
}

// ─── User Edit Modal ──────────────────────────────────────────────────────
function UserEditModal({
  user,
  tenants,
  callerRole,
  onClose,
  onSaved,
}: {
  user: AdminUser | null;
  tenants: WhitelistedDomain[];
  callerRole: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [role, setRole] = useState<'user' | 'admin'>(
    user?.role === 'superadmin' ? 'admin' : (user?.role ?? 'user'),
  );
  const [allowedDomains, setAllowedDomains] = useState<string[]>(user?.allowedDomains ?? []);
  const [allDomains, setAllDomains] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const roleEditable = user ? canChangeRole(callerRole, user) : false;
  const isTuringUser = user?.email.endsWith('@turing.com') ?? false;

  useEffect(() => {
    if (user?.role === 'admin' && user.allowedDomains === null) {
      setAllDomains(true);
    }
  }, [user]);

  const activeTenants = tenants.filter((t) => t.isActive);

  const toggleDomain = (domain: string) => {
    setAllowedDomains((prev) =>
      prev.includes(domain) ? prev.filter((d) => d !== domain) : [...prev, domain],
    );
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError('');
    try {
      const data: Parameters<typeof updateUser>[1] = {};
      if (roleEditable && role !== user.role) data.role = role;
      if (role === 'admin' || user.role === 'admin') {
        data.allowedDomains = allDomains ? null : allowedDomains;
      }
      if (firstName !== (user.firstName ?? '')) data.firstName = firstName;
      if (lastName !== (user.lastName ?? '')) data.lastName = lastName;
      await updateUser(user.id, data);
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg mx-4 my-12 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit User</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{user?.email}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">First Name</span>
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name"
                className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Last Name</span>
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name"
                className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </label>
          </div>

          {/* Role selector */}
          {user?.role === 'superadmin' ? (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
              <p className="text-sm text-purple-700 dark:text-purple-300">Superadmin role cannot be changed from this interface.</p>
            </div>
          ) : roleEditable ? (
            <div>
              <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Role</span>
              {!isTuringUser && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">Only @turing.com users can be promoted to admin.</p>
              )}
              <div className="flex gap-2">
                {(['user', 'admin'] as const).map((r) => {
                  const cfg = ROLE_CONFIG[r];
                  const Icon = cfg.icon;
                  const disabled = r === 'admin' && !isTuringUser;
                  return (
                    <button key={r} type="button" onClick={() => !disabled && setRole(r)}
                      disabled={disabled}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        disabled ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700 text-gray-400' :
                        role === r
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 ring-2 ring-blue-500/20'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}>
                      <Icon className="w-4 h-4" />{cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div>
              <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Role</span>
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                {(() => { const cfg = ROLE_CONFIG[user?.role ?? 'user']; const Icon = cfg.icon; return <><Icon className="w-4 h-4 text-gray-400" /><span className="text-sm text-gray-500 dark:text-gray-400">{cfg.label}</span></>; })()}
                <span className="text-xs text-gray-400 ml-auto">Not editable</span>
              </div>
            </div>
          )}

          {/* Allowed Domains — show for admin role (current or being set to) */}
          {((role === 'admin' && roleEditable) || user?.role === 'admin') && user?.role !== 'superadmin' && (
            <div>
              <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Allowed Domains</span>
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input type="checkbox" checked={allDomains} onChange={(e) => setAllDomains(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="text-sm text-gray-700 dark:text-gray-300">Access to all domains</span>
              </label>
              {!allDomains && (
                <div className="space-y-1.5 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2">
                  {activeTenants.length === 0 && <p className="text-xs text-gray-400 p-2">No active tenants available.</p>}
                  {activeTenants.map((t) => (
                    <label key={t.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${
                        allowedDomains.includes(t.name || t.domain) ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}>
                      <input type="checkbox" checked={allowedDomains.includes(t.name || t.domain)} onChange={() => toggleDomain(t.name || t.domain)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t.name || t.domain}</span>
                      <span className="text-xs text-gray-400 ml-auto">{t.domain}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Dialog ──────────────────────────────────────────────────────
function ConfirmDialog({
  title, message, confirmLabel, destructive, onConfirm, onCancel,
}: {
  title: string; message: string; confirmLabel: string; destructive?: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 space-y-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
        </div>
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Sort helpers ─────────────────────────────────────────────────────────
const ROLE_ORDER: Record<string, number> = { superadmin: 0, admin: 1, user: 2 };

function compareUsers(a: AdminUser, b: AdminUser, col: SortCol, dir: SortDir): number {
  let cmp = 0;
  switch (col) {
    case 'email':
      cmp = a.email.localeCompare(b.email);
      break;
    case 'role':
      cmp = (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9);
      break;
    case 'status':
      cmp = (a.isActive === b.isActive) ? 0 : a.isActive ? -1 : 1;
      break;
    case 'lastLogin': {
      const aT = a.lastLoginTime ? new Date(a.lastLoginTime).getTime() : 0;
      const bT = b.lastLoginTime ? new Date(b.lastLoginTime).getTime() : 0;
      cmp = aT - bT;
      break;
    }
    case 'created': {
      const aC = new Date(a.createdAt).getTime();
      const bC = new Date(b.createdAt).getTime();
      cmp = aC - bC;
      break;
    }
  }
  return dir === 'asc' ? cmp : -cmp;
}

// ─── Main Section ─────────────────────────────────────────────────────────
export function AccessManagementSection() {
  const { tenants: contextTenants } = useAdminData();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [tenants, setTenants] = useState<WhitelistedDomain[]>(contextTenants);
  const [callerRole, setCallerRole] = useState<string>('admin');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [sortCol, setSortCol] = useState<SortCol>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<AdminUser | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const currentUser = useMemo(() => {
    try {
      const stored = localStorage.getItem('current_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await listUsers();
      setUsers(data.users);
      setCallerRole(data.callerRole);
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { setTenants(contextTenants); }, [contextTenants]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSort = (col: SortCol) => {
    if (sortCol === col) {
      setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    let list = [...users];

    // Text search
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((u) =>
        u.email.toLowerCase().includes(q) ||
        u.firstName?.toLowerCase().includes(q) ||
        u.lastName?.toLowerCase().includes(q)
      );
    }

    // Column filters
    if (statusFilter !== 'all') {
      list = list.filter((u) => statusFilter === 'active' ? u.isActive : !u.isActive);
    }
    if (roleFilter !== 'all') {
      list = list.filter((u) => u.role === roleFilter);
    }

    // Sort
    list.sort((a, b) => compareUsers(a, b, sortCol, sortDir));

    return list;
  }, [users, search, statusFilter, roleFilter, sortCol, sortDir]);

  const activeUsers = users.filter((u) => u.isActive);
  const adminCount = users.filter((u) => u.role === 'admin' || u.role === 'superadmin').length;
  const userCount = users.filter((u) => u.role === 'user').length;

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setActionLoading(true);
    try {
      await updateUser(deactivateTarget.id, { isActive: false });
      setDeactivateTarget(null);
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to deactivate');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (u: AdminUser) => {
    setActionLoading(true);
    try {
      await updateUser(u.id, { isActive: true });
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to reactivate');
    } finally {
      setActionLoading(false);
    }
  };

  const activeFilterCount = (statusFilter !== 'all' ? 1 : 0) + (roleFilter !== 'all' ? 1 : 0);

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-6 transition-opacity duration-300">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Access Management</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage users and their platform access.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <button
          onClick={() => { setStatusFilter('all'); setRoleFilter('all'); }}
          className={`text-left bg-white dark:bg-gray-900 rounded-xl border p-4 transition-colors cursor-pointer ${statusFilter === 'all' && roleFilter === 'all' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`}
        >
          <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{users.length}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Total Users</div>
        </button>
        <button
          onClick={() => { setStatusFilter(statusFilter === 'active' && roleFilter === 'all' ? 'all' : 'active'); setRoleFilter('all'); }}
          className={`text-left bg-white dark:bg-gray-900 rounded-xl border p-4 transition-colors cursor-pointer ${statusFilter === 'active' && roleFilter === 'all' ? 'border-green-500 ring-2 ring-green-500/20' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`}
        >
          <div className="text-2xl font-semibold text-green-600">{activeUsers.length}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Active</div>
        </button>
        <button
          onClick={() => { setRoleFilter(roleFilter === 'admin' ? 'all' : 'admin'); setStatusFilter('all'); }}
          className={`text-left bg-white dark:bg-gray-900 rounded-xl border p-4 transition-colors cursor-pointer ${roleFilter === 'admin' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`}
        >
          <div className="text-2xl font-semibold text-blue-600">{adminCount}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Admins</div>
        </button>
        <button
          onClick={() => { setRoleFilter(roleFilter === 'user' ? 'all' : 'user'); setStatusFilter('all'); }}
          className={`text-left bg-white dark:bg-gray-900 rounded-xl border p-4 transition-colors cursor-pointer ${roleFilter === 'user' ? 'border-gray-500 ring-2 ring-gray-500/20' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`}
        >
          <div className="text-2xl font-semibold text-gray-500">{userCount}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Regular Users</div>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-2">
          <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Search + active filter chips */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name..."
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 dark:text-gray-400">Filters:</span>
            {roleFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                Role: {ROLE_CONFIG[roleFilter]?.label || roleFilter}
                <button onClick={() => setRoleFilter('all')} className="hover:text-blue-900 dark:hover:text-blue-100">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                Status: {statusFilter === 'active' ? 'Active' : 'Inactive'}
                <button onClick={() => setStatusFilter('all')} className="hover:text-blue-900 dark:hover:text-blue-100">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button onClick={() => { setRoleFilter('all'); setStatusFilter('all'); }}
              className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-16 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {search || roleFilter !== 'all' || statusFilter !== 'all' ? 'No users match your filters.' : 'No users found.'}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {sorted.length} user{sorted.length !== 1 ? 's' : ''}
              {sorted.length !== users.length && ` of ${users.length}`}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <SortHeader label="User" col="email" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <ColumnFilterDropdown
                  label="Role"
                  value={roleFilter}
                  options={[
                    { value: 'all', label: 'All Roles' },
                    { value: 'superadmin', label: 'Superadmin' },
                    { value: 'admin', label: 'Admin' },
                    { value: 'user', label: 'User' },
                  ]}
                  onChange={(v) => setRoleFilter(v as any)}
                />
                <ColumnFilterDropdown
                  label="Status"
                  value={statusFilter}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  onChange={(v) => setStatusFilter(v as any)}
                />
                <th className="px-4 py-3">Allowed Domains</th>
                <SortHeader label="Last Login" col="lastLogin" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <SortHeader label="Created" col="created" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {sorted.map((u) => {
                const roleCfg = ROLE_CONFIG[u.role] || ROLE_CONFIG.user;
                const RoleIcon = roleCfg.icon;
                const displayName = [u.firstName, u.lastName].filter(Boolean).join(' ');
                return (
                  <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{u.email}</div>
                        {displayName && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{displayName}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${roleCfg.color}`}>
                        <RoleIcon className="w-3 h-3" />{roleCfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        u.isActive
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500'
                      }`}>
                        {u.isActive ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {u.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.role === 'superadmin' ? (
                        <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">All Domains</span>
                      ) : u.role === 'admin' ? (
                        u.allowedDomains === null ? (
                          <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">All Domains</span>
                        ) : u.allowedDomains && u.allowedDomains.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {u.allowedDomains.map((d) => (
                              <span key={d} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">{d}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">None assigned</span>
                        )
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{formatDateTime(u.lastLoginTime)}</td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{formatDate(u.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {canEditUser(callerRole, u) && (
                          <button onClick={() => setEditTarget(u)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors" title="Edit">
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {u.isActive && canDeactivateUser(callerRole, u, currentUser?.id) && (
                          <button onClick={() => setDeactivateTarget(u)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors" title="Deactivate">
                            <Power className="w-4 h-4" />
                          </button>
                        )}
                        {!u.isActive && canDeactivateUser(callerRole, u, currentUser?.id) && (
                          <button onClick={() => handleReactivate(u)} disabled={actionLoading}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors disabled:opacity-50" title="Reactivate">
                            <Power className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {editTarget && (
        <ModalPortal>
          <UserEditModal user={editTarget} tenants={tenants} callerRole={callerRole} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); fetchUsers(); }} />
        </ModalPortal>
      )}
      {deactivateTarget && (
        <ModalPortal>
          <ConfirmDialog
            title="Deactivate User"
            message={`This will deactivate "${deactivateTarget.email}". They will lose access to the platform. You can reactivate them later.`}
            confirmLabel="Deactivate" destructive onConfirm={handleDeactivate} onCancel={() => setDeactivateTarget(null)} />
        </ModalPortal>
      )}
    </div>
  );
}
